/**
 * AuditLogFilters Component
 * Filter controls for audit log queries: date range, user, action type
 */

import React from 'react';
import { AuditLogFilters as FiltersType, AuditUser, AuditAction } from '../../types/audit';

interface AuditLogFiltersProps {
  filters: FiltersType;
  users: AuditUser[];
  loadingUsers: boolean;
  onFilterChange: (filters: Partial<FiltersType>) => void;
  onApply: () => void;
}

const ACTION_OPTIONS: { value: AuditAction | ''; label: string }[] = [
  { value: '', label: 'All Actions' },
  { value: 'LOGIN', label: 'Login' },
  { value: 'LOGOUT', label: 'Logout' },
  { value: 'ACCESS_DENIED', label: 'Access Denied' },
  { value: 'PROJECTION_EDIT', label: 'Projection Edit' },
  { value: 'AGENT_KEY_CREATE', label: 'Agent Setup Code Created' },
  { value: 'AGENT_KEY_REDEEM', label: 'Agent Connected' },
  { value: 'AGENT_KEY_REVOKE', label: 'Agent Disconnected' },
];

export const AuditLogFilters: React.FC<AuditLogFiltersProps> = ({
  filters,
  users,
  loadingUsers,
  onFilterChange,
  onApply,
}) => {
  const inputClass = 'border border-gray-300 rounded px-2 py-1.5 text-sm bg-white shadow-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500';
  const labelClass = 'block text-xs font-medium text-gray-600 mb-1';

  return (
    <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 mb-4">
      <div className="flex flex-wrap items-end gap-4">
        {/* Start Date */}
        <div>
          <label className={labelClass}>Start Date</label>
          <input
            type="date"
            value={filters.startDate}
            onChange={(e) => onFilterChange({ startDate: e.target.value })}
            className={inputClass}
          />
        </div>

        {/* End Date */}
        <div>
          <label className={labelClass}>End Date</label>
          <input
            type="date"
            value={filters.endDate}
            onChange={(e) => onFilterChange({ endDate: e.target.value })}
            className={inputClass}
          />
        </div>

        {/* User Filter */}
        <div>
          <label className={labelClass}>User</label>
          <select
            value={filters.userId || ''}
            onChange={(e) => onFilterChange({ userId: e.target.value || undefined })}
            disabled={loadingUsers}
            className={`${inputClass} min-w-[180px] disabled:bg-gray-100`}
          >
            <option value="">All Users</option>
            {users.map((user) => (
              <option key={user.userId} value={user.userId}>
                {user.displayName}
              </option>
            ))}
          </select>
        </div>

        {/* Action Filter */}
        <div>
          <label className={labelClass}>Action</label>
          <select
            value={filters.action || ''}
            onChange={(e) => onFilterChange({ action: (e.target.value as AuditAction) || undefined })}
            className={`${inputClass} min-w-[140px]`}
          >
            {ACTION_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>

        {/* Apply Button */}
        <div>
          <button
            onClick={onApply}
            className="px-4 py-1.5 bg-blue-600 text-white text-sm font-medium rounded hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1"
          >
            Apply Filters
          </button>
        </div>
      </div>
    </div>
  );
};
